import axios, { AxiosHeaders, Method } from "axios";

const baseURL =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_ALPHA_BASE_LOCAL_URL
    : process.env.REACT_APP_ALPHA_BASE_URL;

// Guard against the deploy-time misconfig where REACT_APP_ALPHA_BASE_URL is
// missing — without this, axios resolves URLs against the app's own origin
// and Amplify's SPA rewrite serves index.html (HTTP 200) for /session etc.,
// which silently looks like a successful API response.
if (!baseURL) {
  // eslint-disable-next-line no-console
  console.error(
    "[mpa-web-registration] REACT_APP_ALPHA_BASE_URL is not set. " +
      "API calls will fail. Set it in Amplify Console > Environment variables."
  );
}

const axiosInstance = axios.create({
  baseURL,
  timeout: 30000,
});

// Reject responses that aren't JSON. Catches the Amplify-SPA-rewrite case
// where the app gets index.html back as if the call succeeded.
axiosInstance.interceptors.response.use((response) => {
  const ct = String(response.headers?.["content-type"] || "").toLowerCase();
  const looksLikeHtml =
    ct.includes("text/html") ||
    (typeof response.data === "string" &&
      response.data.trim().startsWith("<"));
  if (looksLikeHtml) {
    throw new Error(
      "API returned HTML instead of JSON — the API base URL is likely " +
        "misconfigured (request reached the SPA, not the backend)."
    );
  }
  return response;
});

const apiClient = async (props: {
  method?: Method;
  url: string;
  data?: Object | FormData;
  headers?: AxiosHeaders;
  params?: Object;
}) => {
  const { method = "post", url, data, headers, params } = props;
  return await axiosInstance({ method, url, data, headers, params });
};

export const appendQueryParams = (
  url: string,
  params: Record<string, string | number | boolean | undefined | null>
): string => {
  const queryString = Object.entries(params)
    .filter(
      ([_, value]) => value !== undefined && value !== null && value !== ""
    )
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
    )
    .join("&");

  const separator = url.includes("?") ? "&" : "?";
  return queryString ? `${url}${separator}${queryString}` : url;
};

export default apiClient;
